import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Surfaces the per-row failure list from a bulk endpoint that returned
 * `{updated: int[], failed: [{id, reason, message}]}`.
 *
 * `result` shape:
 *   { updated: int[], failed: [{id, reason, message}] }
 *
 * Renders nothing when both arrays are empty.
 */
const BulkResultDialog = ({ open, result, onClose, totalRequested }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    if (!result) return null;

    const updated = result.updated || [];
    const failed = result.failed || [];
    const total = totalRequested ?? updated.length + failed.length;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'نتيجة العملية الجماعية' : 'Bulk action result'}
            </DialogTitle>
            <DialogContent dividers>
                <Alert
                    severity={failed.length === 0 ? 'success' : updated.length === 0 ? 'error' : 'warning'}
                    sx={{ mb: 2 }}
                >
                    <Typography variant="body2" fontWeight={600}>
                        {isArabic
                            ? `تم تحديث ${updated.length} من ${total}`
                            : `Updated ${updated.length} of ${total}`}
                    </Typography>
                </Alert>

                {failed.length > 0 && (
                    <>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                            {isArabic ? 'الأسطر التي فشلت' : 'Failed rows'}
                        </Typography>
                        <Box sx={{ maxHeight: 320, overflowY: 'auto', border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>{isArabic ? 'السبب' : 'Reason'}</TableCell>
                                        <TableCell>{isArabic ? 'الرسالة' : 'Message'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {failed.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                    {row.id}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                    {row.reason || '—'}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="caption">
                                                    {row.message || '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button variant="contained" onClick={onClose}>
                    {isArabic ? 'إغلاق' : 'Close'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkResultDialog;
